import { RcFile } from 'antd/lib/upload';
import { IPavement } from '../../store/ducks/fabrication-mockups/types';
import {
  EProjectRevisionStatus,
  IFabricationProject,
  IFabricationProjectResponse
} from '../../store/ducks/fabrication-projects/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import { sanitizeFileName } from '../../utils/sanitizeUtils';

export class FabricationProjectsService extends EGerdauService {
  async getFabricationProjects(
    contractNumber: string
  ): Promise<IFabricationProjectResponse> {
    const headers = this.getHeaders();
    const projectsResponse: IFabricationProjectResponse = {
      projects: []
    };

    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.PROJECTS}/${contractNumber}`,
      { headers }
    );
    projectsResponse.projects = response.data;
    this.setProjectsRevisionStatus(projectsResponse);
    this.sortFabricationProjects(projectsResponse);
    this.sortProjectsPavementsByPosition(projectsResponse);
    return projectsResponse;
  }

  private setProjectsRevisionStatus(
    projectsResponse: IFabricationProjectResponse
  ) {
    projectsResponse.projects?.forEach(
      (project) =>
        (project.revisionStatus = project.nextRevisionId
          ? EProjectRevisionStatus.OLD_REVISION
          : EProjectRevisionStatus.CURRENT_REVISION)
    );
  }

  private sortFabricationProjects(
    projectsResponse: IFabricationProjectResponse
  ) {
    projectsResponse.projects?.sort(
      (a, b) =>
        new Date(b.revisionDateTime || b.creationDate).getTime() -
        new Date(a.revisionDateTime || a.creationDate).getTime()
    );
  }

  private sortProjectsPavementsByPosition(
    projectsResponse: IFabricationProjectResponse
  ) {
    const sortPavementsByPosition = (pavements: IPavement[]) => {
      pavements.sort((p1, p2) => p2.position - p1.position);
    };

    projectsResponse.projects?.forEach((p) => {
      return sortPavementsByPosition(p.pavements);
    });
  }

  static async uploadProject(
    contractNumber: string,
    structures: string[],
    revision: string,
    file: RcFile,
    pavements?: number[],
    previousProjectId?: number
  ): Promise<IFabricationProject> {
    const headers = new EGerdauService().getHeaders();
    const bodyRequest = {
      pavements,
      structures,
      revision,
      previousProjectId
    };
    const sanitizedFileName = sanitizeFileName(file.name);
    const sanitizedFile = new File([file], sanitizedFileName, {
      type: file.type
    });

    const formData = new FormData();

    formData.append('project', JSON.stringify(bodyRequest));
    formData.append('file', sanitizedFile);
    formData.append('Content-Type', 'multipart/form-data');

    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.PROJECTS}/${contractNumber}`,
      formData,
      { headers }
    );
    const fabricationProject = response.data as IFabricationProject;
    return fabricationProject;
  }

  static async removeProject(contractNumber: string, projectId: number) {
    const headers = new EGerdauService().getHeaders();
    await customersAuthorizationApi.delete(
      `${ECustomerAuthorizationEndpoints.PROJECTS}/${contractNumber}/${projectId}`,
      { headers }
    );
  }

  async downloadProject(contractNumber: string, projectId: number) {
    const headers = super.getHeaders();
    await customersAuthorizationApi.delete(
      `${ECustomerAuthorizationEndpoints.PROJECTS}/${contractNumber}/${projectId}`,
      { headers }
    );
  }

  static async downloadProject(
    contractNumber: string,
    projectId: number
  ): Promise<string> {
    const headers = new EGerdauService().getHeaders();
    const downloadCQresponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.PROJECTS}/${contractNumber}/${projectId}`,
      { headers }
    );

    return downloadCQresponse.data;
  }

  static async updateProject(
    contractNumber: string,
    projectId: number,
    structures: string[],
    revision: string,
    pavements?: number[]
  ) {
    const headers = new EGerdauService().getHeaders();
    const bodyRequest = {
      structures,
      revision,
      pavements
    };

    const formData = new FormData();
    formData.append('project', JSON.stringify(bodyRequest));
    formData.append('Content-Type', 'multipart/form-data');
    await customersAuthorizationApi.put(
      `${ECustomerAuthorizationEndpoints.PROJECTS}/${contractNumber}/${projectId}`,
      formData,
      { headers }
    );
  }
}
